import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  downloadWorkshopReportV1PulseWorkshopsWorkshopIdReportHtmlGet,
  useCloseWorkshopV1PulseWorkshopsWorkshopIdClosePost,
  useCreatePublicSubmissionV1PulsePublicOrganizationSlugWorkshopKeySubmissionsPost,
  useCreateWorkshopV1PulseWorkshopsPost,
  useDeleteWorkshopV1PulseWorkshopsWorkshopIdDelete,
  useGenerateWorkshopReportV1PulseWorkshopsWorkshopIdReportPost,
  useGetPublicWorkshopV1PulsePublicOrganizationSlugWorkshopKeyGet,
  useGetWorkshopAnalysisV1PulseWorkshopsWorkshopIdAnalysisGet,
  useGetWorkshopReportV1PulseWorkshopsWorkshopIdReportGet,
  useGetWorkshopV1PulseWorkshopsWorkshopIdGet,
  useListWorkshopsV1PulseWorkshopsGet,
  useListWorkshopSubmissionsV1PulseWorkshopsWorkshopIdSubmissionsGet,
  useReopenWorkshopV1PulseWorkshopsWorkshopIdReopenPost,
  useRotateWorkshopLinkV1PulseWorkshopsWorkshopIdRotateLinkPost,
  useUpdateWorkshopSubmissionV1PulseWorkshopsWorkshopIdSubmissionsSubmissionIdPatch,
  useUpdateWorkshopV1PulseWorkshopsWorkshopIdPatch,
} from '../generated/pulse/pulse';
import type {
  CountSignal,
  PulseAnalysis as GeneratedPulseAnalysis,
  PulseMaturityStage as GeneratedPulseMaturityStage,
  PulseOption as GeneratedPulseOption,
  PulseSkillProfile,
  PulseSubmissionCreate,
  PulseSubmissionCreateRole,
  PulseSubmissionCreateSubmissionPerspective,
  PulseSubmissionInclusionUpdate,
  PulseSubmissionResponse,
  PulseWorkshopCreate,
  PulseWorkshopCreateTracksItem,
  PulseWorkshopListItem,
  PulseWorkshopResponse,
  PulseWorkshopUpdate,
} from '../generated/models';

export type PulseTrack = PulseWorkshopCreateTracksItem;
export type CreatePulseWorkshopRequest = PulseWorkshopCreate;
export type UpdatePulseWorkshopRequest = PulseWorkshopUpdate;
export type PulseOption = GeneratedPulseOption;
export type PulseMaturityStage = GeneratedPulseMaturityStage;
export type PulseRole = PulseSubmissionCreateRole;
export type PulsePerspective = PulseSubmissionCreateSubmissionPerspective;
export type PulseSkillProfileRequest = PulseSkillProfile;
export type PulseSubmissionRequest = PulseSubmissionCreate;
export type PulseSubmission = PulseSubmissionResponse;
export type PulseAnalysis = GeneratedPulseAnalysis;
export type PulseCountSignal = CountSignal;
export type PulseWorkshop = PulseWorkshopResponse &
  Partial<
    Pick<
      PulseWorkshopListItem,
      | 'submission_count'
      | 'included_submission_count'
      | 'excluded_submission_count'
      | 'latest_activity_at'
      | 'report_status'
    >
  >;
export type UpdatePulseSubmissionInclusionRequest = PulseSubmissionInclusionUpdate & {
  submissionId: number;
};

export const PULSE_TRACKS: ReadonlyArray<{ value: PulseTrack; label: string }> = [
  { value: 'engineering', label: 'Engineering' },
  { value: 'engineering-leadership', label: 'Engineering leadership' },
  { value: 'product', label: 'Product' },
  { value: 'business-ops', label: 'Business / operations' },
];

const workshopListKey = ['pulse', 'workshops'] as const;
const pulseWorkshopKey = (workshopId: number) =>
  ['pulse', 'workshops', workshopId] as const;

function useWorkshopMutationCache() {
  const queryClient = useQueryClient();
  return {
    queryClient,
    onSuccess: (workshop: PulseWorkshopResponse) => {
      queryClient.setQueryData(pulseWorkshopKey(workshop.id), workshop);
      queryClient.invalidateQueries({ queryKey: workshopListKey });
      queryClient.invalidateQueries({
        queryKey: ['pulse', 'workshops', workshop.id, 'report'],
      });
    },
  };
}

export function usePulseWorkshops(limit = 50, offset = 0) {
  return useListWorkshopsV1PulseWorkshopsGet(
    { limit, offset },
    { query: { queryKey: [...workshopListKey, limit, offset] } },
  );
}

export function usePulseWorkshop(workshopId: number) {
  return useGetWorkshopV1PulseWorkshopsWorkshopIdGet(workshopId, {
    query: {
      queryKey: pulseWorkshopKey(workshopId),
      enabled: Number.isFinite(workshopId) && workshopId > 0,
    },
  });
}

export function useCreatePulseWorkshop() {
  const { queryClient, onSuccess } = useWorkshopMutationCache();
  const mutation = useCreateWorkshopV1PulseWorkshopsPost(
    { mutation: { onSuccess } },
    queryClient,
  );
  return {
    ...mutation,
    mutateAsync: (request: CreatePulseWorkshopRequest) =>
      mutation.mutateAsync({ data: request }),
  };
}

export function useUpdatePulseWorkshop(workshopId: number) {
  const { queryClient, onSuccess } = useWorkshopMutationCache();
  const mutation = useUpdateWorkshopV1PulseWorkshopsWorkshopIdPatch(
    { mutation: { onSuccess } },
    queryClient,
  );
  return {
    ...mutation,
    mutateAsync: (request: UpdatePulseWorkshopRequest) =>
      mutation.mutateAsync({
        workshopId,
        data: request,
      }),
  };
}

export function useClosePulseWorkshop() {
  const { queryClient, onSuccess } = useWorkshopMutationCache();
  const mutation = useCloseWorkshopV1PulseWorkshopsWorkshopIdClosePost(
    { mutation: { onSuccess } },
    queryClient,
  );
  return {
    ...mutation,
    mutateAsync: (workshopId: number) => mutation.mutateAsync({ workshopId }),
  };
}

export function useReopenPulseWorkshop() {
  const { queryClient, onSuccess } = useWorkshopMutationCache();
  const mutation = useReopenWorkshopV1PulseWorkshopsWorkshopIdReopenPost(
    { mutation: { onSuccess } },
    queryClient,
  );
  return {
    ...mutation,
    mutateAsync: (workshopId: number) => mutation.mutateAsync({ workshopId }),
  };
}

export function useRotatePulseWorkshopLink() {
  const { queryClient, onSuccess } = useWorkshopMutationCache();
  const mutation = useRotateWorkshopLinkV1PulseWorkshopsWorkshopIdRotateLinkPost(
    { mutation: { onSuccess } },
    queryClient,
  );
  return {
    ...mutation,
    mutateAsync: (workshopId: number) => mutation.mutateAsync({ workshopId }),
  };
}

export function useDeletePulseWorkshop() {
  const queryClient = useQueryClient();
  const mutation = useDeleteWorkshopV1PulseWorkshopsWorkshopIdDelete({
    mutation: {
      onSuccess: (_, variables) => {
        queryClient.removeQueries({
          queryKey: pulseWorkshopKey(variables.workshopId),
        });
        queryClient.invalidateQueries({ queryKey: workshopListKey });
      },
    },
  });
  return {
    ...mutation,
    mutateAsync: (workshopId: number) => mutation.mutateAsync({ workshopId }),
  };
}

export function usePublicPulseWorkshop(
  organizationSlug: string,
  workshopKey: string,
) {
  return useGetPublicWorkshopV1PulsePublicOrganizationSlugWorkshopKeyGet(
    organizationSlug,
    workshopKey,
    {
      query: {
        queryKey: ['pulse', 'public', organizationSlug, workshopKey],
        enabled: Boolean(organizationSlug && workshopKey),
        retry: false,
      },
    },
  );
}

export function useSubmitPulseResponse(
  organizationSlug: string,
  workshopKey: string,
) {
  const mutation =
    useCreatePublicSubmissionV1PulsePublicOrganizationSlugWorkshopKeySubmissionsPost();
  return {
    ...mutation,
    mutateAsync: (request: PulseSubmissionRequest) =>
      mutation.mutateAsync({
        organizationSlug,
        workshopKey,
        data: request,
      }),
  };
}

export function usePulseSubmissions(workshopId: number, limit = 50, offset = 0) {
  return useListWorkshopSubmissionsV1PulseWorkshopsWorkshopIdSubmissionsGet(
    workshopId,
    { limit, offset },
    {
      query: {
        queryKey: [
          'pulse',
          'workshops',
          workshopId,
          'submissions',
          limit,
          offset,
        ],
        enabled: Number.isFinite(workshopId) && workshopId > 0,
      },
    },
  );
}

export function useUpdatePulseSubmissionInclusion(workshopId: number) {
  const queryClient = useQueryClient();
  const mutation =
    useUpdateWorkshopSubmissionV1PulseWorkshopsWorkshopIdSubmissionsSubmissionIdPatch({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['pulse', 'workshops', workshopId, 'submissions'],
          });
          queryClient.invalidateQueries({
            queryKey: pulseWorkshopKey(workshopId),
          });
          queryClient.invalidateQueries({
            queryKey: ['pulse', 'workshops', workshopId, 'analysis'],
          });
          queryClient.invalidateQueries({
            queryKey: ['pulse', 'workshops', workshopId, 'report'],
          });
          queryClient.invalidateQueries({ queryKey: workshopListKey });
        },
      },
    });
  return {
    ...mutation,
    mutateAsync: ({
      submissionId,
      included,
      reason,
    }: UpdatePulseSubmissionInclusionRequest) =>
      mutation.mutateAsync({
        workshopId,
        submissionId,
        data: { included, reason },
      }),
  };
}

export function usePulseAnalysis(workshopId: number) {
  return useGetWorkshopAnalysisV1PulseWorkshopsWorkshopIdAnalysisGet(workshopId, {
    query: {
      queryKey: ['pulse', 'workshops', workshopId, 'analysis'],
      enabled: Number.isFinite(workshopId) && workshopId > 0,
    },
  });
}

export function usePulseReport(workshopId: number) {
  return useGetWorkshopReportV1PulseWorkshopsWorkshopIdReportGet(workshopId, {
    query: {
      queryKey: ['pulse', 'workshops', workshopId, 'report'],
      enabled: Number.isFinite(workshopId) && workshopId > 0,
    },
  });
}

export function useGeneratePulseReport(workshopId: number) {
  const queryClient = useQueryClient();
  const mutation = useGenerateWorkshopReportV1PulseWorkshopsWorkshopIdReportPost({
    mutation: {
      onSuccess: report => {
        queryClient.setQueryData(
          ['pulse', 'workshops', workshopId, 'report'],
          report,
        );
        queryClient.invalidateQueries({ queryKey: workshopListKey });
      },
    },
  });
  return {
    ...mutation,
    mutateAsync: () => mutation.mutateAsync({ workshopId }),
  };
}

export function useDownloadPulseReport(workshopId: number) {
  return useMutation<Blob, Error, void>({
    mutationFn: async () => {
      const html =
        await downloadWorkshopReportV1PulseWorkshopsWorkshopIdReportHtmlGet(
          workshopId,
        );
      return new Blob([String(html)], { type: 'text/html;charset=utf-8' });
    },
  });
}
